import { zeroErrorMessage } from "@/lib/zero-error";
import { useQuery as useZeroQuery, useZero } from "@rocicorp/zero/react";
import { appMutators, appQueries } from "@production-hub/shared";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { toFriendlyMutationError } from "@/lib/error-utils";
import { generateUUID } from "@/lib/utils";
import {
  calculateLineItemTotal as _calcLineItemTotal,
  calculateGroupTotal as _calcGroupTotal,
  calculateFinanceTotals,
} from "@/lib/finance-calculations";
import type {
  BaseGroupEntity,
  BaseLineItemEntity,
  BaseDeductionItemEntity,
  LineItemType,
} from "@/types/finance-entities";

export const calculateLineItemTotal = _calcLineItemTotal;
export const calculateGroupTotal = _calcGroupTotal;

export type { BaseGroupEntity, BaseLineItemEntity, BaseDeductionItemEntity };

export interface QuoteGroupEntity extends BaseGroupEntity {
  quote_version_id: string;
}

export interface QuoteLineItemEntity extends BaseLineItemEntity {
  quote_group_id: string;
}

export interface QuoteDeductionItemEntity extends BaseDeductionItemEntity {
  quote_version_id: string;
}

export interface QuoteGroupWithItems extends QuoteGroupEntity {
  items: QuoteLineItemEntity[];
}

type ZeroMutationResult =
  | { type: "error"; error: { message: string } }
  | { type: string; error?: never };
type ZeroMutateClient = {
  mutate: (mutation: unknown) => {
    client: Promise<void>;
    server: Promise<ZeroMutationResult>;
  };
};
type MutationOptions<T> = { onSuccess?: (data: T) => void };

type ZeroQuoteGroupRow = {
  id: string;
  quoteVersionID: string;
  name: string;
  displayOrder: number;
  isOptional: boolean;
  applyProjectDiscount: boolean;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
  items?: ZeroQuoteLineItemRow[];
};

type ZeroQuoteLineItemRow = {
  id: string;
  quoteGroupID: string;
  name: string;
  amount: number;
  factor: number;
  unitPrice: number;
  discount: number;
  notes: string;
  externalNote?: string | null;
  displayOrder: number;
  isTitle: boolean;
  lineItemType: LineItemType;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
};

type ZeroQuoteDeductionItemRow = {
  id: string;
  quoteVersionID: string;
  description: string;
  amount: number;
  displayOrder: number;
  sourceInvoiceID?: string | null;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
};

function toLegacyTimestamp(value: number | string | null | undefined) {
  if (typeof value === "number") return new Date(value).toISOString();
  return value ?? "";
}

function toLegacyLineItem(row: ZeroQuoteLineItemRow): QuoteLineItemEntity {
  return {
    id: row.id,
    quote_group_id: row.quoteGroupID,
    name: row.name,
    amount: row.amount,
    factor: row.factor,
    unit_price: row.unitPrice,
    discount: row.discount,
    notes: row.notes,
    external_note: row.externalNote ?? "",
    display_order: row.displayOrder,
    is_title: row.isTitle,
    line_item_type: row.lineItemType,
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
  };
}

function toLegacyGroup(row: ZeroQuoteGroupRow): QuoteGroupWithItems {
  return {
    id: row.id,
    quote_version_id: row.quoteVersionID,
    name: row.name,
    display_order: row.displayOrder,
    is_optional: row.isOptional,
    apply_project_discount: row.applyProjectDiscount,
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
    items: [...(row.items ?? [])]
      .sort((a, b) => a.displayOrder - b.displayOrder)
      .map(toLegacyLineItem),
  };
}

function toLegacyDeduction(
  row: ZeroQuoteDeductionItemRow,
): QuoteDeductionItemEntity {
  return {
    id: row.id,
    quote_version_id: row.quoteVersionID,
    description: row.description,
    amount: row.amount,
    display_order: row.displayOrder,
    source_invoice_id: row.sourceInvoiceID ?? null,
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
  };
}

async function reportZeroMutationFailure(
  serverResult: Promise<ZeroMutationResult>,
  fallback: string,
) {
  const result = await serverResult;
  if (result.type === "error") {
    throw new Error(result.error?.message || fallback);
  }
}

function useZeroMutation<TInput, TResult>({
  run,
  failure,
}: {
  run: (input: TInput) => Promise<TResult>;
  failure: string;
}) {
  const [isPending, setIsPending] = useState(false);

  const wrappedRun = async (input: TInput) => {
    setIsPending(true);
    try {
      return await run(input);
    } catch (error) {
      throw toFriendlyMutationError(error);
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (input: TInput, options?: MutationOptions<TResult>) => {
      void wrappedRun(input)
        .then((data) => options?.onSuccess?.(data))
        .catch((error) => toast.error(`${failure}: ${error.message}`));
    },
    mutateAsync: wrappedRun,
    isPending,
  };
}

export function calculateQuoteGroupsTotals(
  groups: QuoteGroupWithItems[],
  projectDiscountPercent: number,
  vatRate: "CH_8_1" | "NONE",
  deductionItems: QuoteDeductionItemEntity[] = [],
) {
  return calculateFinanceTotals(
    groups,
    projectDiscountPercent,
    vatRate,
    deductionItems,
  );
}

export function useQuoteVersionGroupsWithItems(
  quoteVersionId: string | undefined,
) {
  const [data, details] = useZeroQuery(
    appQueries.quoteGroups.byVersion({
      quoteVersionID: quoteVersionId ?? "00000000-0000-0000-0000-000000000000",
    }),
  );
  const groups = useMemo(
    () =>
      ((data ?? []) as ZeroQuoteGroupRow[])
        .map(toLegacyGroup)
        .sort((a, b) => a.display_order - b.display_order),
    [data],
  );

  return {
    data: quoteVersionId ? groups : [],
    isLoading:
      !!quoteVersionId && details.type !== "complete" && groups.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useQuoteVersionDeductionItems(
  quoteVersionId: string | undefined,
) {
  const [data, details] = useZeroQuery(
    appQueries.quoteDeductionItems.byVersion({
      quoteVersionID: quoteVersionId ?? "00000000-0000-0000-0000-000000000000",
    }),
  );
  const deductions = useMemo(
    () =>
      ((data ?? []) as ZeroQuoteDeductionItemRow[])
        .map(toLegacyDeduction)
        .sort((a, b) => a.display_order - b.display_order),
    [data],
  );

  return {
    data: quoteVersionId ? deductions : [],
    isLoading:
      !!quoteVersionId &&
      details.type !== "complete" &&
      deductions.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useCreateQuoteGroup() {
  const zero = useZero() as ZeroMutateClient;
  return useZeroMutation<
    {
      quoteVersionId: string;
      name?: string;
      displayOrder?: number;
      isOptional?: boolean;
      applyProjectDiscount?: boolean;
      optimisticId?: string;
    },
    QuoteGroupEntity
  >({
    failure: "Failed to create group",
    run: async (vars) => {
      const id = vars.optimisticId ?? generateUUID();
      const now = new Date().toISOString();
      const mutation = zero.mutate(
        appMutators.quoteGroups.create({
          id,
          quoteVersionID: vars.quoteVersionId,
          name: vars.name ?? "New Group",
          displayOrder: vars.displayOrder ?? 0,
          isOptional: vars.isOptional ?? false,
          applyProjectDiscount: vars.applyProjectDiscount ?? true,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(mutation.server, "Group create rejected");
      return {
        id,
        quote_version_id: vars.quoteVersionId,
        name: vars.name ?? "New Group",
        display_order: vars.displayOrder ?? 0,
        is_optional: vars.isOptional ?? false,
        apply_project_discount: vars.applyProjectDiscount ?? true,
        created_at: now,
        updated_at: now,
      };
    },
  });
}

export function useUpdateQuoteGroup() {
  const zero = useZero() as ZeroMutateClient;
  return useZeroMutation<
    {
      id: string;
      updates: Partial<{
        name: string;
        display_order: number;
        is_optional: boolean;
        apply_project_discount: boolean;
      }>;
    },
    { id: string }
  >({
    failure: "Failed to update group",
    run: async ({ id, updates }) => {
      const mutation = zero.mutate(
        appMutators.quoteGroups.update({
          id,
          name: updates.name,
          displayOrder: updates.display_order,
          isOptional: updates.is_optional,
          applyProjectDiscount: updates.apply_project_discount,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(mutation.server, "Group update rejected");
      return { id };
    },
  });
}

export function useDeleteQuoteGroup() {
  const zero = useZero() as ZeroMutateClient;
  return useZeroMutation<string, string>({
    failure: "Failed to delete group",
    run: async (id) => {
      const mutation = zero.mutate(appMutators.quoteGroups.delete({ id }));
      await mutation.client;
      await reportZeroMutationFailure(mutation.server, "Group delete rejected");
      return id;
    },
  });
}

export function useDuplicateQuoteGroup() {
  const zero = useZero() as ZeroMutateClient;
  return useZeroMutation<
    { groupId: string; group?: QuoteGroupWithItems },
    { id: string }
  >({
    failure: "Failed to duplicate group",
    run: async ({ groupId, group }) => {
      const newID = generateUUID();
      const itemIDs =
        group?.items.map((item) => ({
          sourceID: item.id,
          newID: generateUUID(),
        })) ?? [];
      const mutation = zero.mutate(
        appMutators.quoteGroups.duplicate({ id: groupId, newID, itemIDs }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Group duplicate rejected",
      );
      return { id: newID };
    },
  });
}

export function useCreateQuoteLineItem() {
  const zero = useZero() as ZeroMutateClient;
  return useZeroMutation<
    {
      quoteGroupId: string;
      quoteVersionId?: string;
      name?: string;
      amount?: number;
      factor?: number;
      unitPrice?: number;
      discount?: number;
      notes?: string;
      displayOrder?: number;
      optimisticId?: string;
      isTitle?: boolean;
      lineItemType?: LineItemType;
    },
    QuoteLineItemEntity
  >({
    failure: "Failed to create line item",
    run: async (vars) => {
      const id = vars.optimisticId ?? generateUUID();
      const now = new Date().toISOString();
      const mutation = zero.mutate(
        appMutators.quoteLineItems.create({
          id,
          quoteGroupID: vars.quoteGroupId,
          name: vars.name ?? "",
          amount: vars.amount ?? 1,
          factor: vars.factor ?? 1,
          unitPrice: vars.unitPrice ?? 0,
          discount: vars.discount ?? 0,
          notes: vars.notes ?? "",
          displayOrder: vars.displayOrder ?? 0,
          isTitle: vars.isTitle ?? false,
          lineItemType: vars.lineItemType ?? "generic",
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Line item create rejected",
      );
      return {
        id,
        quote_group_id: vars.quoteGroupId,
        name: vars.name ?? "",
        amount: vars.amount ?? 1,
        factor: vars.factor ?? 1,
        unit_price: vars.unitPrice ?? 0,
        discount: vars.discount ?? 0,
        notes: vars.notes ?? "",
        external_note: "",
        display_order: vars.displayOrder ?? 0,
        is_title: vars.isTitle ?? false,
        line_item_type: vars.lineItemType ?? "generic",
        created_at: now,
        updated_at: now,
      };
    },
  });
}

export function useUpdateQuoteLineItem() {
  const zero = useZero() as ZeroMutateClient;
  return useZeroMutation<
    {
      id: string;
      updates: Partial<{
        quote_group_id: string;
        name: string;
        amount: number;
        factor: number;
        unit_price: number;
        discount: number;
        notes: string;
        external_note: string;
        display_order: number;
        line_item_type: LineItemType;
      }>;
    },
    { id: string }
  >({
    failure: "Failed to update line item",
    run: async ({ id, updates }) => {
      const mutation = zero.mutate(
        appMutators.quoteLineItems.update({
          id,
          quoteGroupID: updates.quote_group_id,
          name: updates.name,
          amount: updates.amount,
          factor: updates.factor,
          unitPrice: updates.unit_price,
          discount: updates.discount,
          notes: updates.notes,
          externalNote: updates.external_note,
          displayOrder: updates.display_order,
          lineItemType: updates.line_item_type,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Line item update rejected",
      );
      return { id };
    },
  });
}

export function useDeleteQuoteLineItem() {
  const zero = useZero() as ZeroMutateClient;
  return useZeroMutation<{ id: string; quoteVersionId?: string }, string>({
    failure: "Failed to delete line item",
    run: async ({ id }) => {
      const mutation = zero.mutate(appMutators.quoteLineItems.delete({ id }));
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Line item delete rejected",
      );
      return id;
    },
  });
}

export function useReorderQuoteLineItems() {
  const zero = useZero() as ZeroMutateClient;
  return useZeroMutation<
    { quoteGroupId: string; orderedIds: string[] },
    { quoteGroupId: string }
  >({
    failure: "Failed to reorder line items",
    run: async ({ quoteGroupId, orderedIds }) => {
      const mutation = zero.mutate(
        appMutators.quoteLineItems.reorder({
          quoteGroupID: quoteGroupId,
          orderedIDs: orderedIds,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Line item reorder rejected",
      );
      return { quoteGroupId };
    },
  });
}

export function useCreateQuoteDeductionItem() {
  const zero = useZero() as ZeroMutateClient;
  return useZeroMutation<
    {
      quoteVersionId: string;
      description?: string;
      amount?: number;
      displayOrder?: number;
    },
    QuoteDeductionItemEntity
  >({
    failure: "Failed to create deduction item",
    run: async (vars) => {
      const id = generateUUID();
      const now = new Date().toISOString();
      const mutation = zero.mutate(
        appMutators.quoteDeductionItems.create({
          id,
          quoteVersionID: vars.quoteVersionId,
          description: vars.description ?? "",
          amount: vars.amount ?? 0,
          displayOrder: vars.displayOrder ?? 0,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Deduction create rejected",
      );
      return {
        id,
        quote_version_id: vars.quoteVersionId,
        description: vars.description ?? "",
        amount: vars.amount ?? 0,
        display_order: vars.displayOrder ?? 0,
        source_invoice_id: null,
        created_at: now,
        updated_at: now,
      };
    },
  });
}

export function useUpdateQuoteDeductionItem() {
  const zero = useZero() as ZeroMutateClient;
  return useZeroMutation<
    {
      id: string;
      updates: Partial<{
        description: string;
        amount: number;
        display_order: number;
      }>;
    },
    { id: string }
  >({
    failure: "Failed to update deduction item",
    run: async ({ id, updates }) => {
      const mutation = zero.mutate(
        appMutators.quoteDeductionItems.update({
          id,
          description: updates.description,
          amount: updates.amount,
          displayOrder: updates.display_order,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Deduction update rejected",
      );
      return { id };
    },
  });
}

export function useDeleteQuoteDeductionItem() {
  const zero = useZero() as ZeroMutateClient;
  return useZeroMutation<{ id: string; quoteVersionId: string }, string>({
    failure: "Failed to delete deduction item",
    run: async ({ id }) => {
      const mutation = zero.mutate(
        appMutators.quoteDeductionItems.delete({ id }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Deduction delete rejected",
      );
      return id;
    },
  });
}

export function useDuplicateVersionContent() {
  const zero = useZero() as ZeroMutateClient;
  return useZeroMutation<
    { sourceVersionId: string; targetVersionId: string },
    { targetVersionId: string }
  >({
    failure: "Failed to duplicate content",
    run: async ({ sourceVersionId, targetVersionId }) => {
      const mutation = zero.mutate(
        appMutators.quoteGroups.duplicateVersionContent({
          sourceVersionID: sourceVersionId,
          targetVersionID: targetVersionId,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Version content duplicate rejected",
      );
      return { targetVersionId };
    },
  });
}
